<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Projects</h5>
        <p class="page-sub">Manage full project lifecycle — planning, execution, and closure</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="$router.push(routePrefix + '/projects/dashboard')">
          <font-awesome-icon icon="gauge" class="me-1" /> Dashboard
        </button>
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="doExport('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Project
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search code or name..." />
      <select v-model="filterStatus" @change="page = 1; fetchProjects()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="active">Active</option>
        <option value="on_hold">On Hold</option>
        <option value="closed">Closed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <select v-model="filterType" @change="page = 1; fetchProjects()" class="filter-select">
        <option value="">All Types</option>
        <option value="fixed_price">Fixed Price</option>
        <option value="time_and_material">T&amp;M</option>
        <option value="cost_plus">Cost Plus</option>
        <option value="retainer">Retainer</option>
      </select>
      <div class="filter-date-group">
        <input type="date" v-model="filterFrom" @change="page = 1; fetchProjects()" class="filter-date" title="Start date from" />
        <span class="filter-date-sep">–</span>
        <input type="date" v-model="filterTo" @change="page = 1; fetchProjects()" class="filter-date" title="End date to" />
      </div>
      <button v-if="filterFrom || filterTo" class="btn-clear-date" @click="filterFrom = ''; filterTo = ''; page = 1; fetchProjects()" title="Clear date filter">&#x2715;</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div><p>Loading...</p>
      </div>
      <div v-else-if="!projects.length" class="empty-state">
        <font-awesome-icon icon="diagram-project" class="empty-icon" />
        <p>No projects found</p>
        <span>Create your first project to get started</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Project Name</th>
            <th>Type</th>
            <th>Manager</th>
            <th>Contract Value</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in projects" :key="p.id">
            <td class="mono">{{ p.project_code }}</td>
            <td>
              <div class="fw-600">{{ p.project_name }}</div>
              <div v-if="p.description" class="text-muted text-sm">{{ truncate(p.description, 55) }}</div>
            </td>
            <td>{{ formatType(p.type) }}</td>
            <td>{{ p.manager ? p.manager.first_name + ' ' + p.manager.last_name : '—' }}</td>
            <td class="amount-col">{{ p.contract_value ? formatMoney(p.contract_value) : '—' }}</td>
            <td>{{ p.start_date ? formatDate(p.start_date) : '—' }}</td>
            <td>{{ p.end_date ? formatDate(p.end_date) : '—' }}</td>
            <td><span :class="['status-badge', `status-badge--${p.status}`]">{{ p.status }}</span></td>
            <td>
              <div class="row-actions">
                <button class="btn-link" @click="$router.push(routePrefix + '/projects/' + p.id)">View</button>
                <button v-if="['draft','active','on_hold'].includes(p.status)" class="btn-link btn-link--edit" @click="openEdit(p)">Edit</button>
                <button v-if="p.status === 'draft'" class="btn-link btn-link--danger" @click="deleteTarget = p">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta && meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box modal-box--lg">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Project' : 'New Project' }}</span>
          <button class="modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Project Name <span class="req">*</span></label>
              <input v-model="form.project_name" class="form-control" placeholder="e.g. Office Renovation Phase 1" />
            </div>
            <div class="form-group">
              <label>Type <span class="req">*</span></label>
              <select v-model="form.type" class="form-control">
                <option value="">Select type</option>
                <option value="fixed_price">Fixed Price</option>
                <option value="time_and_material">T&amp;M</option>
                <option value="cost_plus">Cost Plus</option>
                <option value="retainer">Retainer</option>
                <option value="internal">Internal</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Billing Method</label>
              <select v-model="form.billing_method" class="form-control">
                <option value="">Select billing method</option>
                <option value="milestone">Milestone</option>
                <option value="time_and_material">T&amp;M</option>
                <option value="fixed">Fixed</option>
                <option value="retainer">Retainer</option>
              </select>
            </div>
            <div class="form-group">
              <label>Revenue Method</label>
              <select v-model="form.revenue_method" class="form-control">
                <option value="cost_to_cost">Cost-to-Cost (PoC)</option>
                <option value="on_billing">On Billing</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Project Manager</label>
              <Multiselect
                v-model="form.manager_id"
                label="name"
                :options="managerOptions"
                :searchable="true"
                :close-on-select="true"
                :create-option="false"
                placeholder="Search manager..."
                class="custom-multiselect"
              />
            </div>
            <div class="form-group">
              <label>Customer</label>
              <Multiselect
                v-model="form.customer_id"
                label="name"
                :options="customerOptions"
                :searchable="true"
                :close-on-select="true"
                :create-option="false"
                placeholder="Search customer..."
                class="custom-multiselect"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Planned Start Date</label>
              <input type="date" v-model="form.start_date" class="form-control" />
            </div>
            <div class="form-group">
              <label>Planned End Date</label>
              <input type="date" v-model="form.end_date" class="form-control" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Actual Start Date</label>
              <input type="date" v-model="form.actual_start_date" class="form-control" />
            </div>
            <div class="form-group">
              <label>Actual End Date</label>
              <input type="date" v-model="form.actual_end_date" class="form-control" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Currency</label>
              <select v-model="form.currency" class="form-control">
                <option value="">Select currency</option>
                <option v-for="c in currencies" :key="c.id" :value="c.code || c.name">
                  {{ c.name }}{{ c.code ? ` (${c.code})` : '' }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Contract Value</label>
              <input type="number" v-model="form.contract_value" class="form-control" placeholder="0.00" />
            </div>
          </div>
          <div class="form-group">
            <label>Not-to-Exceed Amount</label>
            <input type="number" v-model="form.not_to_exceed" class="form-control" placeholder="Optional NTE cap" />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="form.description" class="form-control" rows="3" placeholder="Project scope and objectives"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="closeModal">Cancel</button>
          <button class="btn-primary" @click="saveProject" :disabled="saving">
            {{ saving ? 'Saving...' : editing ? 'Update Project' : 'Create Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Delete Project</span>
          <button class="modal-close" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="modal-body">
          <p>Delete <strong>{{ deleteTarget.project_code }}</strong> — <em>{{ deleteTarget.project_name }}</em>?</p>
          <p class="text-muted text-sm">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import debounce from 'lodash/debounce';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

export default {
  name: 'ProjectsIndex',
  components: { Multiselect },
  setup() { return { toast: useToast() }; },
  data() {
    return {
      projects: [], meta: null, loading: false, saving: false,
      search: '', filterStatus: '', filterType: '', filterFrom: '', filterTo: '', page: 1,
      exportMenuOpen: false, exporting: false,
      deleteTarget: null, deleting: false,
      modalOpen: false, editing: null,
      employees: [], customers: [], currencies: [],
      form: this.blankForm(),
    };
  },
  computed: {
    routePrefix() {
      const code = this.$route.params.company_code;
      return code ? `/${code}` : '';
    },
    managerOptions() {
      return this.employees.map(e => ({ value: e.id, name: `${e.first_name} ${e.last_name}` }));
    },
    customerOptions() {
      return this.customers.map(c => ({ value: c.id, name: c.customer_name }));
    },
  },
  created() {
    this.debouncedFetch = debounce(() => { this.page = 1; this.fetchProjects(); }, 400);
    this.fetchProjects();
    document.addEventListener('click', this.onClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside);
  },
  methods: {
    blankForm() {
      return { project_name: '', type: '', billing_method: '', revenue_method: 'cost_to_cost', manager_id: '', customer_id: '', start_date: '', end_date: '', actual_start_date: '', actual_end_date: '', currency: '', contract_value: '', not_to_exceed: '', description: '' };
    },
    async fetchProjects() {
      this.loading = true;
      try {
        const { data } = await axios.get('/projects', { params: { search: this.search, status: this.filterStatus, type: this.filterType, from: this.filterFrom, to: this.filterTo, page: this.page, per_page: 20 } });
        this.projects = data.data?.data ?? [];
        this.meta = data.data ?? null;
      } catch { this.toast.error('Failed to load projects.'); }
      finally { this.loading = false; }
    },
    async openCreate() {
      this.editing = null;
      this.form = this.blankForm();
      await this.loadDropdowns();
      this.modalOpen = true;
    },
    async openEdit(project) {
      this.editing = project.id;
      this.form = {
        ...project,
        start_date:        project.start_date        ? project.start_date.slice(0, 10)        : '',
        end_date:          project.end_date          ? project.end_date.slice(0, 10)          : '',
        actual_start_date: project.actual_start_date ? project.actual_start_date.slice(0, 10) : '',
        actual_end_date:   project.actual_end_date   ? project.actual_end_date.slice(0, 10)   : '',
      };
      await this.loadDropdowns();
      this.modalOpen = true;
    },
    async loadDropdowns() {
      if (this.employees.length) return;
      const [emp, cust, curr] = await Promise.all([
        axios.get('/payroll/employees/active'),
        axios.get('/settings/customers/all'),
        axios.get('/settings/currency'),
      ]);
      this.employees  = emp.data?.data ?? [];
      this.customers  = cust.data?.data ?? [];
      this.currencies = curr.data?.data?.data ?? curr.data?.data ?? [];
    },
    async saveProject() {
      if (!this.form.project_name || !this.form.type) {
        return this.toast.error('Project name and type are required.');
      }
      this.saving = true;
      try {
        if (this.editing) {
          await axios.put(`/projects/${this.editing}`, this.form);
          this.toast.success('Project updated.');
        } else {
          await axios.post('/projects', this.form);
          this.toast.success('Project created.');
        }
        this.closeModal();
        this.fetchProjects();
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save project.');
      } finally { this.saving = false; }
    },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search)       params.search = this.search;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterType)   params.type   = this.filterType;
        if (this.filterFrom)   params.from   = this.filterFrom;
        if (this.filterTo)     params.to     = this.filterTo;
        const { data } = await axios.get('/projects/export', { params, responseType: 'blob' });
        const ext = format === 'pdf' ? 'pdf' : 'csv';
        const ts  = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const url = URL.createObjectURL(data);
        const a   = document.createElement('a');
        a.href = url; a.download = `projects_${ts}.${ext}`; a.click();
        URL.revokeObjectURL(url);
      } catch { this.toast.error('Export failed.'); }
      finally  { this.exporting = false; }
    },
    onClickOutside(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        await axios.delete(`/projects/${this.deleteTarget.id}`);
        this.toast.success('Project deleted.');
        this.deleteTarget = null;
        this.fetchProjects();
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to delete project.');
      } finally { this.deleting = false; }
    },
    closeModal() { this.modalOpen = false; },
    changePage(p) { this.page = p; this.fetchProjects(); },
    truncate(str, len) { return str && str.length > len ? str.slice(0, len) + '…' : str; },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    formatMoney(v) { return new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2 }).format(v); },
    formatType(t) { return (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.me-1 { margin-right:4px; }

/* ── Filter bar ──────────────────────────────────────────────────────── */
.filter-bar    { display:flex; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.filter-input  { flex:1; min-width:200px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; cursor:pointer; outline:none; }
.filter-date-group { display:flex; align-items:center; gap:6px; border:1.5px solid #dde5f0; border-radius:8px; padding:0 10px; background:#fff; }
.filter-date { border:none; outline:none; font-size:13px; padding:8px 0; background:transparent; cursor:pointer; }
.filter-date-sep { color:#aab8cc; font-size:13px; }
.btn-clear-date { border:none; background:none; color:#aab8cc; font-size:14px; cursor:pointer; padding:6px 4px; line-height:1; }
.btn-clear-date:hover { color:#e53e3e; }

/* ── Card / table ───────────────────────────────────────────────────── */
.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tbody tr:last-child td { border-bottom:none; }
.mono { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.amount-col { text-align:right; font-variant-numeric:tabular-nums; }
.fw-600 { font-weight:600; }
.text-muted { color:#aab8cc; font-size:12px; }

/* ── Status badge ───────────────────────────────────────────────────── */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--active    { background:#dcfce7; color:#15803d; }
.status-badge--on_hold   { background:#fff7ed; color:#c2410c; }
.status-badge--closed    { background:#f1f5f9; color:#475569; }
.status-badge--cancelled { background:#fee2e2; color:#dc2626; }

/* ── Row actions / buttons ──────────────────────────────────────────── */
.row-actions { display:flex; gap:6px; }
.btn-link { background:none; border:none; font-size:12.5px; font-weight:600; color:#004AAD; cursor:pointer; padding:4px 6px; border-radius:5px; }
.btn-link:hover { background:#eff6ff; }
.btn-link--edit { color:#0369a1; }
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }

/* ── Pagination ─────────────────────────────────────────────────────── */
.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:16px; border-top:1px solid #f0f4f8; }
.pg-btn  { padding:7px 14px; border:1px solid #dde5f0; border-radius:7px; background:#fff; cursor:pointer; font-size:13px; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; }
.pg-info { font-size:13px; color:#7a90b0; }

/* ── Modal ──────────────────────────────────────────────────────────── */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:none; align-items:center; justify-content:center; padding:20px; }
.modal-overlay.open { display:flex; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--lg { max-width:720px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; position:sticky; top:0; background:#fff; z-index:1; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close  { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body   { padding:20px 24px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; position:sticky; bottom:0; background:#fff; }

/* ── Form ───────────────────────────────────────────────────────────── */
.form-row  { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.form-group { margin-bottom:14px; }
.form-group label { display:block; font-size:12.5px; font-weight:600; color:#4a5568; margin-bottom:5px; }
.form-control { width:100%; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-control:focus { border-color:#004AAD; }
.req { color:#dc2626; }

/* ── Spinner ────────────────────────────────────────────────────────── */
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

@media (max-width:768px) {
  .form-row { grid-template-columns:1fr; }
  .data-table th:nth-child(4), .data-table td:nth-child(4),
  .data-table th:nth-child(5), .data-table td:nth-child(5) { display:none; }
}

.btn-link--danger { color:#e53e3e; }
.btn-link--danger:hover { color:#b91c1c; }
.btn-danger { padding:9px 18px; background:#e53e3e; color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-danger:hover { background:#c53030; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }

/* ── Export button ──────────────────────────────────────────────────── */
.export-wrap  { position:relative; }
.btn-export   { display:flex; align-items:center; gap:6px; padding:9px 14px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; font-weight:600; color:#1a2b45; cursor:pointer; white-space:nowrap; }
.btn-export:hover { border-color:#004AAD; color:#004AAD; }
.btn-export:disabled { opacity:.6; cursor:not-allowed; }
.export-menu  { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; overflow:hidden; }
.export-opt   { display:flex; align-items:center; width:100%; padding:10px 16px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; text-align:left; }
.export-opt:hover { background:#f4f7fb; }

/* ── Multiselect overrides ───────────────────────────────────────────── */
.custom-multiselect { --ms-font-size:13px; --ms-line-height:1.5; --ms-min-height:40px; --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-ring-color:rgba(0,74,173,.2); --ms-dropdown-max-height:220px; --ms-option-font-size:13px; }
</style>
